// Small modal: pick which account pays for an installment. Auto-selects if only one account.
import { useEffect, useState } from "react";
import { X, Wallet } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { useAccounts } from "@/lib/useAccounts";
import { useCurrencyTotal, convertAmount } from "@/lib/useCurrencyTotal";

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

export function InstallmentPayAccountModal({ open, amount, onConfirm, onClose, busy }) {
  const { lang } = useI18n();
  const { user } = useAuth();
  const homeCcy = (user?.currency || "AED").toUpperCase();
  const { items = [] } = useAccounts();
  const entries = items.map((a) => ({ amount: a.balance ?? a.opening_balance, currency: a.currency }));
  const { rates } = useCurrencyTotal(entries, homeCcy);
  const [sel, setSel] = useState("");
  useEffect(() => { if (open && items.length === 1) setSel(items[0].id); }, [open, items]);
  if (!open) return null;
  const AR = lang === "ar";
  return (
    <div data-testid="inst-pay-account-modal" className="fixed inset-0 z-[70] bg-black/40 backdrop-blur-md grid place-items-center p-4" onClick={onClose}>
      <div className="w-full max-w-sm neu rounded-3xl p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl grid place-items-center bg-indigo-100 text-indigo-600"><Wallet className="w-5 h-5" /></div>
          <div className="flex-1 min-w-0">
            <p className="font-display font-extrabold text-base neu-accent-blue">{AR ? "من أي حساب؟" : "Pay from?"}</p>
            <p className="text-[11px] text-slate-500 truncate">{AR ? "المبلغ" : "Amount"}: {fmt(amount)} {homeCcy}</p>
          </div>
          <button data-testid="inst-pay-account-close" onClick={onClose} className="p-1.5 rounded-lg text-slate-500"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {items.map((a) => {
            const raw = a.balance ?? a.opening_balance;
            const conv = convertAmount(raw, a.currency, homeCcy, rates);
            const shown = conv == null ? raw : conv;
            return (
              <button key={a.id} data-testid={`inst-pay-acc-${a.id}`} onClick={() => setSel(a.id)}
                className={`w-full text-start p-3 rounded-2xl flex items-center justify-between gap-2 ${sel === a.id ? "neu-fill-blue text-white" : "neu-sm"}`}>
                <span className="font-bold truncate">{a.name}</span>
                <span className="tabular-nums text-xs opacity-80 shrink-0">{fmt(shown)} {homeCcy}</span>
              </button>
            );
          })}
          {items.length === 0 && <p className="text-center text-sm text-slate-500 py-4">{AR ? "لا حسابات — أضف حساب أولاً" : "No accounts — add one first"}</p>}
        </div>
        <button data-testid="inst-pay-account-confirm" disabled={!sel || busy} onClick={() => onConfirm(sel)}
          className="w-full py-3 rounded-2xl neu-fill-blue text-white font-semibold shadow-lg disabled:opacity-60">
          {busy ? "…" : (AR ? "تأكيد الدفع" : "Confirm payment")}
        </button>
      </div>
    </div>
  );
}
